import { computed } from 'vue'
import { useRuntimeConfig, useRoute, useI18n, useHead, useRequestURL, useState } from '#imports'

export const useCanonicalUrl = () => {
    const route = useRoute()
    const { locale } = useI18n()
    const runtimeConfig = useRuntimeConfig()

    // Locale-prefixed path that, when set, replaces the request path in canonicalUrl.
    // Used by the product detail page to honor Shopware's canonicalProduct reference.
    const canonicalPathOverride = useState<string | null>('canonical-url-override', () => null)

    // Capture origin/path at setup time. useRequestURL() relies on useNuxtApp() and
    // must not be called inside a computed re-evaluated outside setup (unhead resolves
    // reactive head values lazily, breaking that contract).
    let initialOrigin: string
    let initialPath: string

    if (import.meta.server) {
        try {
            const requestURL = useRequestURL()
            initialOrigin = requestURL.origin
            initialPath = requestURL.pathname
        } catch (e) {
            console.error(e)
            initialOrigin = 'https://www.all4golf.de'
            initialPath = route.path
        }
    } else {
        initialOrigin = window.location.origin
        initialPath = window.location.pathname
    }

    /**
     * Generate canonical URL using the current request's origin
     *
     * Special rule: For .de domain with /en locale, use .com domain instead
     * to avoid duplicate content (e.g., all4golf.de/en/path -> all4golf.com/path)
     */
    const canonicalUrl = computed(() => {
        let origin = initialOrigin
        let path = canonicalPathOverride.value ?? initialPath

        // Special rule: .de/en should point to .com
        const currentLocale = String(locale.value)
        const isDeMarket = origin.includes('all4golf.de')
        const isEnglishLocale = currentLocale === 'en'
        const defaultLocale = runtimeConfig.public.defaultLocale

        if (isDeMarket && isEnglishLocale && defaultLocale == null) {
            // Use .com domain and remove /en prefix from path
            origin = 'https://www.all4golf.com'
            const localePrefix = new RegExp(`^/${currentLocale}`)
            path = path.replace(localePrefix, '') || '/'
        }

        // Remove trailing slash if present (except for root path)
        const normalizedPath = path === '/' ? '' : path.replace(/\/$/, '')
        return `${origin}${normalizedPath}`
    })

    /**
     * Register the canonical link in <head>. Reactive — updates if a child
     * component later sets an override via overrideCanonicalUrl().
     * Resets any prior override so it does not leak between navigations.
     */
    const setCanonicalUrl = () => {
        canonicalPathOverride.value = null
        useHead({
            link: computed(() => [
                {
                    rel: 'canonical',
                    href: canonicalUrl.value
                }
            ])
        })
    }

    /**
     * Replace the canonical URL with the given locale-prefixed path.
     * The .de/en -> .com special rule still applies on top.
     */
    const overrideCanonicalUrl = (path: string) => {
        canonicalPathOverride.value = path
    }

    return {
        canonicalUrl,
        setCanonicalUrl,
        overrideCanonicalUrl
    }
}
